import React from "react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { aboutData, personalInfo } from "../data/mock";
import { SectionReveal } from "./SectionReveal";

const AnimatedHighlight = ({ text, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.span
      ref={ref}
      className="inline-block px-3 py-1.5 bg-emerald-500/8 border border-emerald-500/15 rounded-md text-emerald-700 dark:text-emerald-400 text-sm font-mono font-medium hover:bg-emerald-500/15 hover:border-emerald-500/30 transition-colors duration-300 cursor-default"
      initial={{ opacity: 0, y: 10 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      whileHover={{ scale: 1.05 }}
    >
      {text}
    </motion.span>
  );
};

const AboutSection = () => {
  const sectionRef = useRef(null);

  return (
    <section
      id="about"
      className="relative py-28 lg:py-36 bg-zinc-50 dark:bg-zinc-950 transition-colors duration-500"
      ref={sectionRef}
      aria-label="About Hit Bhalodia"
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-emerald-500/20 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-start">
          {/* Left column */}
          <div>
            <SectionReveal animation="slideRight">
              <span className="text-sm font-mono text-emerald-600/80 dark:text-emerald-400/80 tracking-widest uppercase">
                About
              </span>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-zinc-900 dark:text-zinc-50 leading-tight tracking-tight">
                Engineering the{" "}
                <span className="text-emerald-600 dark:text-emerald-400">open web</span>
              </h2>
            </SectionReveal>

            <SectionReveal animation="blurIn" delay={0.2}>
              <div className="mt-8 space-y-5">
                <p className="text-lg text-zinc-600 dark:text-zinc-400 leading-relaxed">
                  {aboutData.bio}
                </p>
                <p className="text-base text-zinc-500 dark:text-zinc-500 leading-relaxed">
                  {aboutData.extendedBio}
                </p>
              </div>
            </SectionReveal>

            <SectionReveal animation="clipReveal" delay={0.4}>
              <div className="mt-10 rounded-xl overflow-hidden border border-zinc-200 dark:border-zinc-800/50 bg-zinc-100 dark:bg-zinc-900/50">
                <div className="relative aspect-video">
                  <img
                    src={personalInfo.workingImage}
                    alt="Hit Bhalodia working"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                  <div
                    className="absolute inset-0 bg-gradient-to-br from-zinc-100 to-zinc-200 dark:from-zinc-900 dark:to-zinc-800 items-center justify-center hidden"
                    style={{ display: "flex" }}
                  >
                    <div className="text-center">
                      <div className="text-3xl font-mono text-emerald-600/30 dark:text-emerald-500/30 mb-2">
                        &lt;/&gt;
                      </div>
                      <p className="text-sm text-zinc-400 dark:text-zinc-600 font-mono">
                        hit-working.jpg
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </SectionReveal>
          </div>

          {/* Right column */}
          <div>
            <SectionReveal animation="slideLeft" delay={0.15}>
              <h3 className="text-sm font-mono text-zinc-400 dark:text-zinc-500 tracking-widest uppercase mb-6">
                Areas of Focus
              </h3>
              <div className="flex flex-wrap gap-2">
                {aboutData.highlights.map((item, i) => (
                  <AnimatedHighlight key={item} text={item} index={i} />
                ))}
              </div>
            </SectionReveal>

            <SectionReveal animation="rotateIn" delay={0.3}>
              <div className="mt-12 space-y-4">
                <div className="bg-white dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800/50 rounded-xl p-6 hover:border-zinc-300 dark:hover:border-zinc-700/50 transition-colors duration-300 shadow-sm dark:shadow-none">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-emerald-600 dark:text-emerald-400 font-mono text-sm font-bold">rt</span>
                    </div>
                    <div>
                      <h4 className="text-zinc-900 dark:text-zinc-100 font-semibold">rtCamp</h4>
                      <p className="text-sm text-zinc-500 mt-1">
                        Senior Software Engineer — Enterprise WordPress agency building at scale.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800/50 rounded-xl p-6 hover:border-zinc-300 dark:hover:border-zinc-700/50 transition-colors duration-300 shadow-sm dark:shadow-none">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-emerald-600 dark:text-emerald-400 font-mono text-sm font-bold">WP</span>
                    </div>
                    <div>
                      <h4 className="text-zinc-900 dark:text-zinc-100 font-semibold">WordPress Core</h4>
                      <p className="text-sm text-zinc-500 mt-1">
                        Active contributor to the platform powering 40%+ of the web.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800/50 rounded-xl p-6 hover:border-zinc-300 dark:hover:border-zinc-700/50 transition-colors duration-300 shadow-sm dark:shadow-none">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-emerald-600 dark:text-emerald-400 font-mono text-sm font-bold">OS</span>
                    </div>
                    <div>
                      <h4 className="text-zinc-900 dark:text-zinc-100 font-semibold">Open Source</h4>
                      <p className="text-sm text-zinc-500 mt-1">
                        Contributing across Core, Gutenberg, WP-CLI, translations, and plugins.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </SectionReveal>

            <SectionReveal animation="fadeUp" delay={0.5}>
              <div className="mt-8 flex items-center gap-2 text-sm text-zinc-400 dark:text-zinc-600">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500/50" />
                <span className="font-mono">{personalInfo.location}</span>
              </div>
            </SectionReveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
